import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 20px;
  overflow: hidden;
  position: relative;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  z-index: 10;
`;

const Title = styled.h1`
  color: #00ff00;
  font-size: 2.2rem;
  margin: 0;
`;

const Button = styled.button`
  background-color: #000;
  color: #00ff00;
  border: 1px solid #00ff00;
  padding: 10px 18px;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #00ff00;
    color: #000;
  }
`;

const MatrixContainer = styled.div`
  position: relative;
  flex-grow: 1;
  overflow: hidden;
  border: 1px solid #00ff00;
  margin-bottom: 10px;
  cursor: move;
`;

const Matrix = styled.div`
  position: absolute;
  display: grid;
  grid-template-columns: repeat(50, 60px);
  grid-template-rows: repeat(50, 60px);
  transform-origin: 0 0;
  transform: scale(${props => props.scale});
  transition: transform 0.1s ease-out;
`;

const NumberCell = styled.div`
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${props => {
    if (props.isHovered) return '6rem';
    if (props.proximityScale > 0) return `${3.5 + (props.proximityScale * 3)}rem`;
    return '3.5rem';
  }};
  color: ${props => {
    if (props.isHovered) return '#00ff00';
    if (props.proximityScale > 0) {
      const alpha = 0.7 + (props.proximityScale * 0.3);
      return `rgba(0, 255, 0, ${alpha})`;
    }
    return 'rgba(0, 255, 0, 0.7)';
  }};
  cursor: pointer;
  transition: all 0.15s ease-out;
  position: relative;
  z-index: ${props => props.isHovered ? 5 : (props.proximityScale > 0 ? 3 : 1)};
  font-weight: ${props => (props.isHovered || props.proximityScale > 0.5) ? 'bold' : 'normal'};
  text-shadow: ${props => {
    if (props.isHovered) return '0 0 10px #00ff00';
    if (props.proximityScale > 0) return `0 0 ${props.proximityScale * 8}px #00ff00`;
    return 'none';
  }};
  
  ${props => props.isSelected && `
    background-color: rgba(0, 255, 0, 0.2);
    box-shadow: 0 0 10px #00ff00;
  `}
  
  ${props => props.isSorted && `
    opacity: 0.3;
  `}
`;

const ZoomIndicator = styled.div`
  position: absolute;
  bottom: 10px;
  right: 10px;
  background-color: rgba(0, 0, 0, 0.7);
  color: #00ff00;
  padding: 8px 12px;
  border: 1px solid #00ff00;
  z-index: 10;
  font-size: 1.2rem;
`;

const Controls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
  z-index: 10;
`;

const BinContainer = styled.div`
  display: flex;
  gap: 20px;
  position: relative;
  z-index: 100;
`;



const BinLid = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 10px;
  background-color: #000;
  border-bottom: 1px solid #00ff00;
  z-index: 5;
  transform-origin: center top;
  transition: transform 0.3s ease-out;
  
  &.open {
    animation: openLid 1.5s ease-out forwards;
  }
  
  @keyframes openLid {
    0% { transform: rotateX(0deg); }
    40% { transform: rotateX(-90deg); }
    80% { transform: rotateX(-90deg); }
    100% { transform: rotateX(0deg); }
  }
`;

const Bin = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  border: 1px solid #00ff00;
  padding: 10px;
  width: 120px;
  height: 100px;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  overflow: hidden;
  perspective: 300px;

  &:hover {
    background-color: rgba(0, 255, 0, 0.1);
  }
  
  &.highlight {
    box-shadow: 0 0 20px #00ff00;
    background-color: rgba(0, 255, 0, 0.15);
    animation: binPulse 0.6s ease-out;
  }
  
  @keyframes binPulse {
    0% { 
      box-shadow: 0 0 5px #00ff00;
      background-color: rgba(0, 255, 0, 0.1);
    }
    50% { 
      box-shadow: 0 0 25px #00ff00;
      background-color: rgba(0, 255, 0, 0.2);
    }
    100% { 
      box-shadow: 0 0 20px #00ff00;
      background-color: rgba(0, 255, 0, 0.15);
    }
  }
`;

const BinLabel = styled.div`
  font-size: 1.3rem;
  margin-bottom: 5px;
  position: relative;
  z-index: 2;
`;

const BinCount = styled.div`
  font-size: 1.8rem;
  position: relative;
  z-index: 2;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 20px;
  background-color: #111;
  border: 1px solid #00ff00;
  margin-top: 20px;
  position: relative;
`;

const Progress = styled.div`
  height: 100%;
  background-color: #00ff00;
  width: ${props => props.percent}%;
  transition: width 0.5s;
`;

const ProgressText = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.percent > 50 ? '#000' : '#00ff00'};
  font-size: 1.2rem;
`;

const Message = styled.div`
  color: #00ff00;
  text-align: center;
  margin-top: 20px;
  font-size: 1.5rem;
  height: 1.5rem;
`;

// Seed-based random number generator for consistent randomness
const seededRandom = (seed) => {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
};

// Generate a matrix of random numbers
const generateNumberMatrix = (rows, cols, rowOffset = 0, colOffset = 0) => {
  const matrix = [];
  
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const actualRow = row + rowOffset;
      const actualCol = col + colOffset;
      
      // Use a mix of position-based seed and true randomness
      // This creates a semi-random pattern that's different each time
      // but still deterministic for each position
      const seed = actualRow * 10000 + actualCol + Date.now() % 10000;
      const randomValue = Math.floor(seededRandom(seed) * 10);
      
      matrix.push({
        id: `${actualRow}-${actualCol}`,
        value: randomValue,
        row: actualRow,
        col: actualCol,
        sorted: false
      });
    }
  }
  
  return matrix;
};

const NumberSorting = ({ onBackToFiles, onLogout, file }) => {
  // Track visible region of the matrix
  const [visibleRegion, setVisibleRegion] = useState({
    rowStart: 0,
    colStart: 0,
    rowEnd: 50,
    colEnd: 50
  });
  
  // Generate numbers based on current visible region
  const [numbers, setNumbers] = useState(() => 
    generateNumberMatrix(50, 50, visibleRegion.rowStart, visibleRegion.colStart)
  );
  
  // Track sorted numbers separately to persist across region changes
  const [sortedNumbers, setSortedNumbers] = useState({});
  
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [hoveredNumber, setHoveredNumber] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [bins, setBins] = useState([
    { id: 1, name: "SCARY", count: 0 },
    { id: 2, name: "HAPPY", count: 0 },
    { id: 3, name: "DAZZLING", count: 0 },
    { id: 4, name: "WORRYING", count: 0 }
  ]);
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState('');
  const [completedNumbers, setCompletedNumbers] = useState(0);
  const [scale, setScale] = useState(0.5);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [floatingNumbers, setFloatingNumbers] = useState([]);
  
  // Track last position to detect when we need to load new regions
  const lastPosition = useRef({ x: 0, y: 0 });
  
  // Effect to update numbers when visible region changes
  useEffect(() => {
    const newNumbers = generateNumberMatrix(
      visibleRegion.rowEnd - visibleRegion.rowStart,
      visibleRegion.colEnd - visibleRegion.colStart,
      visibleRegion.rowStart,
      visibleRegion.colStart
    );
    
    // Apply sorted state from our persistent sortedNumbers map
    const updatedNumbers = newNumbers.map(n => ({
      ...n,
      sorted: sortedNumbers[n.id] || false
    }));
    
    setNumbers(updatedNumbers);
  }, [visibleRegion, sortedNumbers]);
  
  // Effect to render floating numbers in their containers
  useEffect(() => {
    // Only process new floating numbers that haven't been rendered yet
    const newFloatingNumbers = floatingNumbers.filter(number => {
      // Check if this element already exists
      return !document.getElementById(`floating-number-${number.id}`);
    });
    
    newFloatingNumbers.forEach(number => {
      const container = document.getElementById(number.containerId);
      if (container) {
        // Create the floating number element
        const floatingEl = document.createElement('div');
        floatingEl.id = `floating-number-${number.id}`;
        floatingEl.textContent = number.value;
        floatingEl.style.position = 'fixed';
        floatingEl.style.fontSize = '4.5rem';
        floatingEl.style.color = '#00ff00';
        floatingEl.style.textShadow = '0 0 10px #00ff00';
        floatingEl.style.pointerEvents = 'none';
        floatingEl.style.zIndex = '9999';
        floatingEl.style.animation = `floatDown-${number.id} 1.8s cubic-bezier(0.215, 0.610, 0.355, 1.000) forwards`;
        
        // Apply delay if specified
        if (number.delay) {
          floatingEl.style.animationDelay = `${number.delay}ms`;
        }
        
        // Add the animation keyframes with different effects for main vs surrounding numbers
        const style = document.createElement('style');
        
        if (number.delay > 0) {
          // For surrounding numbers - smaller, with random offsets
          const randomOffsetX = (Math.random() * 20) - 10;
          const randomOffsetY = (Math.random() * 20) - 10;
          style.textContent = `
            @keyframes floatDown-${number.id} {
              0% { 
                transform: translate(${number.startX}px, ${number.startY}px) scale(1.5); 
                opacity: 1;
                text-shadow: 0 0 15px #00ff00;
              }
              50% {
                transform: translate(${number.startX * 0.5 + randomOffsetX}px, ${number.startY * 0.6 + randomOffsetY}px) scale(1.2);
                opacity: 0.9;
                text-shadow: 0 0 12px #00ff00;
              }
              80% {
                transform: translate(${number.startX * 0.2 + randomOffsetX/2}px, ${number.startY * 0.3 + randomOffsetY/2}px) scale(1);
                opacity: 0.8;
              }
              100% { 
                transform: translate(${randomOffsetX/4}px, ${randomOffsetY/4}px) scale(0.6); 
                opacity: 0.5;
                text-shadow: 0 0 5px #00ff00;
              }
            }
          `;
        } else {
          // For main selected number - larger, more dramatic
          style.textContent = `
            @keyframes floatDown-${number.id} {
              0% { 
                transform: translate(${number.startX}px, ${number.startY}px) scale(2); 
                opacity: 1;
                text-shadow: 0 0 15px #00ff00;
              }
              50% {
                transform: translate(${number.startX * 0.5}px, ${number.startY * 0.6}px) scale(1.5);
                opacity: 0.9;
                text-shadow: 0 0 12px #00ff00;
              }
              80% {
                transform: translate(${number.startX * 0.2}px, ${number.startY * 0.3}px) scale(1.2);
                opacity: 0.8;
              }
              100% { 
                transform: translate(0, 0) scale(0.8); 
                opacity: 0.5;
                text-shadow: 0 0 5px #00ff00;
              }
            }
          `;
        }
        
        document.head.appendChild(style);
        container.appendChild(floatingEl);
        
        // Position the floating number relative to the bin
        const binElements = document.querySelectorAll('.bin');
        binElements.forEach(el => {
          if (parseInt(el.dataset.id) === number.binId) {
            const rect = el.getBoundingClientRect();
            const centerX = rect.left + rect.width / 2;
            const centerY = rect.top + rect.height / 2;
            floatingEl.style.left = `${centerX}px`;
            floatingEl.style.top = `${centerY}px`;
          }
        });
      }
    });
  }, [floatingNumbers]);
  
  const matrixRef = useRef(null);
  const containerRef = useRef(null);
  
  // Calculate proximity to mouse cursor (for radius effect)
  const calculateProximity = (row, col) => {
    if (!mousePosition.x || !mousePosition.y) return 0;
    
    // Calculate cell position
    const cellX = col * 60 * scale + position.x;
    const cellY = row * 60 * scale + position.y;
    
    // Calculate distance to mouse
    const distance = Math.sqrt(
      Math.pow(mousePosition.x - cellX, 2) + 
      Math.pow(mousePosition.y - cellY, 2)
    );
    
    // Very tight proximity effect radius (adjusted by scale)
    const radius = 40 / scale;
    
    if (distance > radius) return 0;
    
    // Calculate proximity scale (1 at center, 0 at radius)
    return 1 - (distance / radius);
  };

  useEffect(() => {
    const percent = (completedNumbers / 100) * 100;
    setProgress(percent > 100 ? 100 : percent);
  }, [completedNumbers]);
  
  // Handle mouse wheel for zooming
  useEffect(() => {
    const handleWheel = (e) => {
      if (containerRef.current && containerRef.current.contains(e.target)) {
        e.preventDefault();
        const delta = e.deltaY * -0.001;
        const newScale = Math.min(Math.max(0.2, scale + delta), 1.0);
        setScale(newScale);
      }
    };
    
    window.addEventListener('wheel', handleWheel, { passive: false });
    return () => window.removeEventListener('wheel', handleWheel);
  }, [scale]);
  
  // Handle mouse events for dragging
  const handleMouseDown = (e) => {
    if (e.button === 0) { // Left mouse button
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };
  
  const handleMouseMoveInMatrix = (e) => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setMousePosition({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
  };
  
  const handleMouseMove = React.useCallback((e) => {
    if (isDragging) {
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;
      setPosition({ x: newX, y: newY });
      
      // Check if we've moved far enough to load new regions
      const deltaX = newX - lastPosition.current.x;
      const deltaY = newY - lastPosition.current.y;
      
      // If we've moved significantly, update the visible region
      if (Math.abs(deltaX) > 60 * scale * 10 || Math.abs(deltaY) > 60 * scale * 10) {
        // Calculate new region based on position and scale
        const cellSize = 60 * scale;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const visibleCols = Math.ceil(viewportWidth / cellSize) + 10; // Add buffer
        const visibleRows = Math.ceil(viewportHeight / cellSize) + 10; // Add buffer
        
        // Calculate which cells should be at the top-left corner
        const colStart = Math.floor(-newX / cellSize) - 5; // Buffer on left
        const rowStart = Math.floor(-newY / cellSize) - 5; // Buffer on top
        
        setVisibleRegion({
          rowStart,
          colStart,
          rowEnd: rowStart + visibleRows,
          colEnd: colStart + visibleCols
        });
        
        lastPosition.current = { x: newX, y: newY };
      }
    }
  }, [isDragging, dragStart.x, dragStart.y, lastPosition, scale]);

  const handleMouseUp = React.useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    } else {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    }
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleNumberHover = (number) => {
    if (!number.sorted) {
      setHoveredNumber(number);
    }
  };
  
  const handleNumberLeave = () => {
    setHoveredNumber(null);
  };

  const handleNumberClick = (number) => {
    if (!number.sorted) {
      setSelectedNumber(number);
      setMessage('');
    }
  };

  const handleBinClick = (bin) => {
    if (selectedNumber === null) {
      setMessage('Please select a number first');
      return;
    }
    
    // Get the position of the selected number element
    const numberElements = document.querySelectorAll('.number-cell');
    let startX = 0;
    let startY = 0;
    let selectedElement = null;
    const surroundingElements = [];
    
    // Find the selected number and its position
    numberElements.forEach(el => {
      if (el.dataset.id === selectedNumber.id) {
        const rect = el.getBoundingClientRect();
        startX = rect.left;
        startY = rect.top;
        selectedElement = el;
      }
    });
    
    // Find surrounding numbers (within proximity)
    if (selectedElement) {
      const selectedRow = parseInt(selectedNumber.id.split('-')[0]);
      const selectedCol = parseInt(selectedNumber.id.split('-')[1]);
      
      // Find surrounding elements
      numberElements.forEach(el => {
        const id = el.dataset.id;
        if (id && id !== selectedNumber.id) {
          const [row, col] = id.split('-').map(Number);
          
          // Check if this is a surrounding number (1 cell away in any direction)
          const rowDiff = Math.abs(row - selectedRow);
          const colDiff = Math.abs(col - selectedCol);
          
          if (rowDiff <= 1 && colDiff <= 1 && !sortedNumbers[id]) {
            surroundingElements.push({
              element: el,
              id,
              rect: el.getBoundingClientRect(),
              value: numbers.find(n => n.id === id)?.value || 0
            });
          }
        }
      });
    }
    
    // Get the position of the bin
    const binElements = document.querySelectorAll('.bin');
    let endX = 0;
    let endY = 0;
    let targetBin = null;
    
    binElements.forEach(el => {
      if (parseInt(el.dataset.id) === bin.id) {
        const rect = el.getBoundingClientRect();
        endX = rect.left + rect.width / 2;
        endY = rect.top + rect.height / 2;
        targetBin = el;
      }
    });
    
    // Add visual highlight to the selected number
    if (selectedElement) {
      selectedElement.style.textShadow = '0 0 20px #00ff00';
      selectedElement.style.color = '#ffffff';
      selectedElement.style.zIndex = '30';
    }
    
    // Create a container for the floating number animation at the document level
    const floatingContainer = document.createElement('div');
    floatingContainer.id = `floating-container-${Date.now()}`;
    floatingContainer.style.position = 'fixed';
    floatingContainer.style.top = '0';
    floatingContainer.style.left = '0';
    floatingContainer.style.width = '100%';
    floatingContainer.style.height = '100%';
    floatingContainer.style.pointerEvents = 'none';
    floatingContainer.style.zIndex = '9999';
    document.body.appendChild(floatingContainer);
    
    // Create floating animations for surrounding numbers with slight delay
    const allFloatingNumbers = [];
    
    // Add main selected number
    allFloatingNumbers.push({
      id: Date.now(),
      value: selectedNumber.value,
      binId: bin.id,
      startX: startX - endX,
      startY: startY - endY,
      containerId: floatingContainer.id,
      delay: 0
    });
    
    // Add surrounding numbers with delays
    surroundingElements.forEach((surround, index) => {
      const delay = 100 + (index * 50); // Stagger the animations
      
      allFloatingNumbers.push({
        id: Date.now() + index + 1,
        value: surround.value,
        binId: bin.id,
        startX: surround.rect.left - endX,
        startY: surround.rect.top - endY,
        containerId: floatingContainer.id,
        delay
      });
      
      // Highlight surrounding elements
      surround.element.style.textShadow = '0 0 15px #00ff00';
      surround.element.style.color = '#ccffcc';
      
      // Mark surrounding numbers as sorted too
      setTimeout(() => {
        setSortedNumbers(prev => ({
          ...prev,
          [surround.id]: true
        }));
        
        // Update current numbers array for surrounding numbers
        const updatedNumbers = numbers.map(n => 
          n.id === surround.id ? { ...n, sorted: true } : n
        );
        setNumbers(updatedNumbers);
      }, 300 + delay);
    });
    
    setFloatingNumbers([...floatingNumbers, ...allFloatingNumbers]);
    
    // Add highlight effect to the bin and open the lid
    if (targetBin) {
      // Find the lid element
      const lidElement = targetBin.querySelector('.bin-lid');
      
      // Add highlight to bin
      targetBin.classList.add('highlight');
      
      // Open the lid
      if (lidElement) {
        lidElement.classList.add('open');
        
        // Close the lid after animation completes
        setTimeout(() => {
          lidElement.classList.remove('open');
        }, 1500);
      }
      
      // Remove highlight after animation completes
      setTimeout(() => {
        targetBin.classList.remove('highlight');
      }, 1000);
    }
    
    // Remove floating numbers and their container after animation completes
    setTimeout(() => {
      // Get the IDs of all floating numbers we just added
      const currentFloatingIds = allFloatingNumbers.map(n => n.id);
      
      // Remove these floating numbers from state
      setFloatingNumbers(prevFloatingNumbers => 
        prevFloatingNumbers.filter(n => !currentFloatingIds.includes(n.id))
      );
      
      // Remove the container from the DOM
      if (floatingContainer && floatingContainer.parentNode) {
        try {
          document.body.removeChild(floatingContainer);
        } catch (e) {
          console.log('Container already removed');
        }
      }
    }, 2000);

    // Mark the number as sorted (with a slight delay for visual effect)
    setTimeout(() => {
      // Add to sorted numbers map to persist across region 
      setSortedNumbers(prev => ({
        ...prev,
        [selectedNumber.id]: true
      }));
      
      // Update the bin count - include main number and surrounding numbers
      const totalSorted = 1 + surroundingElements.length;
      const updatedBins = bins.map(b => {
        if (b.id === bin.id) {
          return { ...b, count: b.count + totalSorted };
        }
        return b;
      });
      setBins(updatedBins);
      
      // Update the number in the matrix
      const updatedNumbers = numbers.map(n => {
        if (n.id === selectedNumber.id) {
          return { ...n, sorted: true };
        }
        return n;
      });
      setNumbers(updatedNumbers);
    }, 300);

    // Update progress
    setCompletedNumbers(completedNumbers + 1);

    // Display a message
    setMessage(`Number ${selectedNumber.value} sorted as ${bin.name}`);

    // Clear selection
    setSelectedNumber(null);

    // Check if 100 is reached
    if (completedNumbers + 1 >= 100) {
      setTimeout(() => {
        setMessage('Daily 100 reached! Well done!');
      }, 1000);
    }
  };

  const handleRefine = () => {
    // Clear sorted numbers
    setSortedNumbers({});
    // Reset position and region
    setPosition({ x: 0, y: 0 });
    setVisibleRegion({
      rowStart: 0,
      colStart: 0,
      rowEnd: 50,
      colEnd: 50
    });
    lastPosition.current = { x: 0, y: 0 };
    // Generate new matrix with fresh randomization
    setNumbers(generateNumberMatrix(50, 50, 0, 0));
    setMessage('New refinement session started');
  };
  
  // Function to randomize a specific region of the matrix
  const randomizeRegion = () => {
    // Randomize just the visible portion
    const newNumbers = numbers.map(n => ({
      ...n,
      value: Math.floor(Math.random() * 10),
      sorted: sortedNumbers[n.id] || false // Preserve sorted state
    }));
    
    setNumbers(newNumbers);
    setMessage('Numbers randomized');
  };

  return (
    <Container>
      <Header>
        <Title>MACRODATA REFINEMENT - {file?.name}</Title>
        <div>
          <Button onClick={onBackToFiles} style={{ marginRight: '10px' }}>BACK TO FILES</Button>
          <Button onClick={onLogout}>LOGOUT</Button>
        </div>
      </Header>

      <MatrixContainer 
        ref={containerRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMoveInMatrix}
        onMouseLeave={() => setMousePosition({ x: 0, y: 0 })}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        <Matrix 
          ref={matrixRef}
          scale={scale}
          style={{ transform: `scale(${scale}) translate(${position.x}px, ${position.y}px)` }}
        >
          {numbers.map(number => (
            <NumberCell 
              key={number.id}
              className="number-cell"
              data-id={number.id}
              isHovered={hoveredNumber && hoveredNumber.id === number.id}
              isSelected={selectedNumber && selectedNumber.id === number.id}
              isSorted={number.sorted}
              proximityScale={!isDragging ? calculateProximity(number.row, number.col) : 0}
              onMouseEnter={() => handleNumberHover(number)}
              onMouseLeave={handleNumberLeave}
              onClick={() => handleNumberClick(number)}
              style={{ 
                gridRow: number.row + 1, 
                gridColumn: number.col + 1,
                opacity: number.sorted ? 0.3 : 1
              }}
            >
              {number.value}
            </NumberCell>
          ))}
        </Matrix>
        <ZoomIndicator>Zoom: {Math.round(scale * 100)}%</ZoomIndicator>
      </MatrixContainer>

      <Controls>
        <div>
          <Button onClick={handleRefine} style={{ marginRight: '10px' }}>NEW REFINEMENT</Button>
          <Button onClick={randomizeRegion}>RANDOMIZE</Button>
        </div>
        
        <BinContainer>
          {bins.map(bin => (
            <Bin 
              key={bin.id} 
              className="bin"
              data-id={bin.id}
              onClick={() => handleBinClick(bin)}
            >

              <BinLid className="bin-lid" />
              <BinLabel>{bin.name}</BinLabel>
              <BinCount>{bin.count}</BinCount>
              
              {/* Floating numbers are now rendered in their own containers */}
            </Bin>
          ))}
        </BinContainer>
      </Controls>

      <ProgressBar>
        <Progress percent={progress} />
        <ProgressText percent={progress}>
          {completedNumbers} / {100} ({Math.round(progress)}%)
        </ProgressText>
      </ProgressBar>

      <Message>{message}</Message>
    </Container>
  );
};

export default NumberSorting;
